import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Home, ShoppingCart, SlidersHorizontal, Search, X,
  ChevronDown, ChevronUp, Plus, Minus,
  MapPin, Globe, User, ShoppingBag, Tag, Menu, Car,
} from "lucide-react";

import logo from "../../../assets/cosmeta/logo.png";

import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import {
  listenToPublishedClassified,
  filterLocalProductsByCountry,
  setFilters,
} from "../../../redux/classifiedsSlice";
import { addToCart, decrementCartItem } from "../../../redux/cartSlice";
import { Notifications, SupportAgent, ChatBubble, ShoppingCartOutlined } from "@mui/icons-material";
import {
  listenToUserNotifications,
  stopUserNotificationsListener,
} from "../../../redux/notificationsSlice";
import {
  electronicsColorAttributes,
  electronicsSizeAttributes,
} from "../../ATTRIBUTES/ElectronicsAttributes";

/* ═══════════════════════════════════════════════════════
   CAR ELECTRONICS CATEGORY IDS
═══════════════════════════════════════════════════════ */
const CAR_CATEGORY_IDS = [20000010]; // Car Electronics

/* ═══════════════════════════════════════════════════════
   DESIGN TOKENS — carbon / racing red automotive theme
═══════════════════════════════════════════════════════ */
const T = {
  bg:           "#FFFFFF",
  bgSoft:       "#FFF5F5",
  bgMuted:      "#FEE2E2",
  border:       "#FECACA",
  muted:        "#7F1D1D",
  mutedSoft:    "#F87171",
  racing:       "#B91C1C",
  racingLight:  "#FCA5A5",
  racingDim:    "rgba(185,28,28,0.12)",
  chrome:       "#EF4444",
  carbon:       "#1C1917",
  red:          "#E50914",
  globalRed:    "#CC0000",
  gold:         "#FEBA02",
  grad:         "linear-gradient(135deg,#1c0a0a 0%,#7F1D1D 38%,#B91C1C 65%,#F87171 100%)",
  gradText:     "linear-gradient(90deg,#B91C1C,#FCA5A5)",
  navy:         "#120606",
  navyMid:      "#1c0a0a",
  racingOutline:"1.5px solid rgba(185,28,28,0.40)",
};

const GRADIENT = T.grad;
const NAVY     = T.navy;

/* EUR formatter */
const fmtEur = (rawRate: number) =>
  `€${rawRate.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/* ═══════════════════════════════════════════════════════
   COSMIC CSS — carbon fibre / racing red palette
═══════════════════════════════════════════════════════ */
const COSMIC_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700&family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  ::-webkit-scrollbar { display: none; }
  input, button { font-family: inherit; }

  @keyframes crSpin {
    0%   { background-position: 0% 50%; }
    50%  { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  @keyframes crAurora {
    0%   { background-position: 0% 50%; }
    50%  { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  @keyframes crShimmer {
    0%   { background-position: -200% center; }
    100% { background-position:  200% center; }
  }
  @keyframes crOrb1 {
    0%   { transform: translate(0,0) scale(1); }
    33%  { transform: translate(60px,-36px) scale(1.10); }
    66%  { transform: translate(-28px,46px) scale(0.94); }
    100% { transform: translate(0,0) scale(1); }
  }
  @keyframes crOrb2 {
    0%   { transform: translate(0,0); }
    33%  { transform: translate(-64px,24px) scale(1.08); }
    66%  { transform: translate(34px,-54px) scale(1.04); }
    100% { transform: translate(0,0); }
  }
  @keyframes crOrb3 {
    0%,100% { transform: translate(0,0) scale(1); }
    50%      { transform: translate(22px,30px) scale(1.06); }
  }
  @keyframes starTwinkle {
    0%,100% { opacity:0.10; transform:scale(0.8); }
    50%     { opacity:0.88; transform:scale(1.4); }
  }
  @keyframes cr-pulse { 0%,100%{opacity:1} 50%{opacity:.3} }
  @keyframes crRev {
    0%,100% { transform: rotate(-5deg) scale(1); }
    50%     { transform: rotate(5deg) scale(1.04); }
  }
  @keyframes crDash {
    0%   { stroke-dashoffset: 200; opacity: 0; }
    20%  { opacity: 1; }
    100% { stroke-dashoffset: 0; opacity: 1; }
  }
  @keyframes crSpeedLine {
    0%   { transform: translateX(0) scaleX(0); opacity: 0; }
    30%  { opacity: 0.5; transform: translateX(0) scaleX(1); }
    100% { transform: translateX(80px) scaleX(0.2); opacity: 0; }
  }

  .cr-cosmic-bg {
    background: linear-gradient(135deg,
      #0a0000 0%, #160202 14%, #0a0000 28%,
      #060000 42%, #100101 58%, #1a0303 74%,
      #0c0101 88%, #0a0000 100%
    );
    background-size: 400% 400%;
    animation: crAurora 20s ease infinite;
  }

  .cr-shimmer-title {
    background: linear-gradient(90deg,
      #B91C1C 0%, #FCA5A5 18%, #fff 36%,
      #F87171 52%, #B91C1C 68%, #FCA5A5 84%, #fff 100%
    );
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: crShimmer 3.8s linear infinite;
  }

  .cr-grad-line {
    background: linear-gradient(90deg, #1c0a0a, #7F1D1D, #B91C1C, #F87171, #B91C1C, #7F1D1D, #1c0a0a);
    background-size: 300% 100%;
    animation: crSpin 3.5s linear infinite;
  }

  .cr-card-hover {
    transition: transform 0.3s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.3s ease;
  }
  .cr-card-hover:hover {
    transform: translateY(-6px);
    box-shadow: 0 24px 56px rgba(185,28,28,0.26), 0 0 0 2px rgba(185,28,28,0.48) !important;
  }

  .cr-sort-item:hover { background: #FEE2E2; }
  input::placeholder { color: rgba(252,165,165,0.36); }
`;

/* ═══════════════════════════════════════════════════════
   STARS — red-tinted
═══════════════════════════════════════════════════════ */
const STARS = Array.from({ length: 34 }, (_, i) => ({
  id: i, size: Math.random() * 2.4 + 0.5,
  x: Math.random() * 100, y: Math.random() * 100,
  delay: Math.random() * 7, dur: 2.2 + Math.random() * 4.8,
  warm: Math.random() > 0.48,
}));

/* speed-line positions */
const SPEED_LINES = [12, 22, 32, 42, 52];

type SortKey = "default" | "price_asc" | "price_desc" | "newest" | "name_asc";
const SORT_OPTS: { key: SortKey; label: string }[] = [
  { key: "default",    label: "Best Match"       },
  { key: "newest",     label: "New Arrivals"      },
  { key: "price_asc",  label: "Price: Low → High" },
  { key: "price_desc", label: "Price: High → Low" },
  { key: "name_asc",   label: "Name: A → Z"       },
];

const CarElectronics: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const authUser         = useSelector((s: RootState) => s.auth.user);
  const authLoaded       = useSelector((s: RootState) => s.auth.authLoaded);
  const userId           = authUser?.userId;
  const notifications    = useSelector((s: RootState) => s.notifications.notifications);
  const unreadCount      = useMemo(() => notifications.filter((n: any) => !n.read).length, [notifications]);
  const cartItems        = useSelector((s: RootState) => s.cart.items);
  const filteredListings = useSelector((s: RootState) => s.classifieds.filteredListings);
  const cartTotal        = cartItems.reduce((s: number, i: any) => s + i.qty, 0);

  const [isMobile,      setIsMobile]      = useState(window.innerWidth <= 768);
  const [searchQuery,   setSearchQuery]   = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [activeSort,    setActiveSort]    = useState<SortKey>("default");
  const [sortOpen,      setSortOpen]      = useState(false);
  const [navOpen,       setNavOpen]       = useState(false);
  const sortBtnRef = useRef<HTMLButtonElement>(null);
  const [sortAnchor, setSortAnchor] = useState<{ top: number; left: number } | null>(null);

  const [userCountry,     setUserCountry]     = useState<string | null>(null);
  const [userCity,        setUserCity]        = useState<string | null>(null);
  const [locationDenied,  setLocationDenied]  = useState(false);
  const [locationLoading, setLocationLoading] = useState(true);
  const [showGlobal,      setShowGlobal]      = useState(false);

  const [variantOpen,   setVariantOpen]   = useState(false);
  const [variantItem,   setVariantItem]   = useState<any | null>(null);
  const [variantMode,   setVariantMode]   = useState<"fashion" | "beauty" | null>(null);
  const [selectedColor, setSelectedColor] = useState<number | null>(null);
  const [selectedSize,  setSelectedSize]  = useState<number | null>(null);
  const [variantQty,    setVariantQty]    = useState(1);

  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 768);
    fn(); window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  useEffect(() => {
    if (!authLoaded) return;
    if (!authUser || !userId) navigate("/AuthSwitch");
  }, [authLoaded, authUser, userId, navigate]);

  useEffect(() => { dispatch(listenToPublishedClassified()); }, [dispatch]);

  useEffect(() => {
    if (!userId) return;
    dispatch(listenToUserNotifications(userId));
    return () => { dispatch(stopUserNotificationsListener()); };
  }, [dispatch, userId]);

  useEffect(() => {
    if (!navigator.geolocation) {
      setLocationDenied(true); setLocationLoading(false);
      dispatch(filterLocalProductsByCountry(null)); return;
    }
    const detect = () => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setLocationDenied(false);
          if ((window as any).google?.maps) {
            const gc = new (window as any).google.maps.Geocoder();
            gc.geocode({ location: { lat: pos.coords.latitude, lng: pos.coords.longitude } }, (results: any, status: string) => {
              if (status === "OK" && results?.[0]) {
                const comps = results[0].address_components;
                const get = (t: string) => comps.find((c: any) => c.types.includes(t))?.long_name ?? null;
                setUserCity(get("locality") || get("administrative_area_level_2"));
                const country = get("country"); setUserCountry(country);
                if (country) dispatch(filterLocalProductsByCountry(country));
              }
              setLocationLoading(false);
            });
          } else { setLocationLoading(false); }
        },
        (err) => {
          if (err.code === err.PERMISSION_DENIED) setLocationDenied(true);
          setLocationLoading(false); dispatch(filterLocalProductsByCountry(null));
        }
      );
    };
    detect();
    if ("permissions" in navigator) {
      navigator.permissions.query({ name: "geolocation" as PermissionName }).then(r => {
        r.onchange = () => { if (r.state === "granted") detect(); };
      });
    }
  }, [dispatch]);

  useEffect(() => {
    if (!userCountry) return;
    dispatch(filterLocalProductsByCountry(showGlobal ? null : userCountry));
  }, [userCountry, showGlobal, dispatch]);

  const handleToggleGlobal = () => {
    const next = !showGlobal; setShowGlobal(next);
    dispatch(setFilters({ city: null, country: null }));
    dispatch(filterLocalProductsByCountry(next ? null : userCountry));
  };

  const getQty = (id: string) => cartItems.find((i: any) => i.listingId === id)?.qty || 0;

  const getVariantMode = (item: any): "fashion" | "beauty" | null => {
    if (!item.variants || !Object.keys(item.variants).length) return null;
    if (Object.values(item.variants).some((v: any) => Array.isArray(v.sizeIds) && v.sizeIds.length)) return "fashion";
    if (Object.values(item.variants).some((v: any) => v.imageUrl && (!v.sizeIds || !v.sizeIds.length))) return "beauty";
    return null;
  };

  const handleAddToCart = (item: any, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (getVariantMode(item) !== null) {
      setVariantItem(item); setVariantMode(getVariantMode(item));
      setSelectedColor(null); setSelectedSize(null); setVariantQty(1); setVariantOpen(true);
      return;
    }
    dispatch(addToCart({ listingId: item.listingId, name: item.name!, price: item.rate!, photo: item.photos?.[0], hostId: item.hostId, brandId: item.brandId }));
  };

  const canContinue =
    variantMode === null ||
    (variantMode === "fashion" && selectedColor !== null && selectedSize !== null && variantQty > 0) ||
    (variantMode === "beauty"  && selectedColor !== null && variantQty > 0);

  const previewColorId = useMemo(() => {
    if (!variantItem || !variantMode) return null;
    const colors = variantItem.attributes?.[variantMode === "beauty" ? 10001 : 20002];
    if (!Array.isArray(colors) || !colors.length) return null;
    return selectedColor ?? colors[0];
  }, [variantItem, variantMode, selectedColor]);

  useEffect(() => {
    if (variantOpen && variantMode === "beauty" && variantItem && selectedColor === null) {
      const colors = variantItem.attributes?.[10001];
      if (Array.isArray(colors) && colors.length) setSelectedColor(colors[0]);
    }
  }, [variantOpen, variantMode, variantItem, selectedColor]);

  const handleVariantContinue = (toCheckout: boolean) => {
    if (!variantItem) return;
    dispatch(addToCart({
      listingId: variantItem.listingId, name: variantItem.name!, price: variantItem.rate!,
      photo: variantItem.variants?.[selectedColor!]?.imageUrl ?? variantItem.photos?.[0],
      colorId: selectedColor!, ...(variantMode === "fashion" && { sizeId: selectedSize! }),
      qty: variantQty, hostId: variantItem.hostId, brandId: variantItem.brandId,
    }));
    setVariantOpen(false); setVariantItem(null); setSelectedColor(null); setSelectedSize(null); setVariantQty(1);
    if (toCheckout) navigate(`/metacheckout/${userId}`, {
      state: { userId, totalQty: cartItems.reduce((s: number, i: any) => s + i.qty, 0) + variantQty, items: cartItems },
    });
  };

  const goToCheckout = () => {
    if (!cartItems.length) { alert("Your cart is empty"); return; }
    navigate(`/metacheckout/${userId}`, {
      state: { userId, totalQty: cartItems.reduce((s: number, i: any) => s + i.qty, 0), totalPrice: cartItems.reduce((s: number, i: any) => s + i.qty * i.price, 0), items: cartItems },
    });
  };

  const goToDetails = (id: string) => navigate(`/metacommercedetails/${id}`);

  const toggleSort = () => {
    if (sortOpen) { setSortOpen(false); setSortAnchor(null); return; }
    if (sortBtnRef.current) {
      const r = sortBtnRef.current.getBoundingClientRect();
      setSortAnchor({ top: r.bottom + 6, left: r.left });
    }
    setSortOpen(true);
  };

  const carProducts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let list = filteredListings.filter((l: any) => {
      if (!l.isPublished) return false;
      if (!CAR_CATEGORY_IDS.includes(l.categoryId)) return false;
      if (q && !`${l.name ?? ""} ${l.description ?? ""}`.toLowerCase().includes(q)) return false;
      return true;
    });
    if (activeSort === "price_asc")  list = [...list].sort((a: any, b: any) => (a.rate ?? 0) - (b.rate ?? 0));
    if (activeSort === "price_desc") list = [...list].sort((a: any, b: any) => (b.rate ?? 0) - (a.rate ?? 0));
    if (activeSort === "name_asc")   list = [...list].sort((a: any, b: any) => (a.name ?? "").localeCompare(b.name ?? ""));
    if (activeSort === "newest")     list = [...list].sort((a: any, b: any) => (b.createdAt ?? 0) - (a.createdAt ?? 0));
    return list;
  }, [filteredListings, searchQuery, activeSort]);

  /* ── HEADER ── */
  const Header = () => (
    <div style={{ position: "sticky", top: 0, zIndex: 200 }}>
      <div className="cr-cosmic-bg" style={{ position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
          {STARS.slice(0, 16).map(s => (
            <div key={s.id} style={{ position: "absolute", left: `${s.x}%`, top: `${s.y}%`, width: s.size, height: s.size, borderRadius: "50%", background: s.warm ? "rgba(252,165,165,0.82)" : "rgba(255,255,255,0.62)", animation: `starTwinkle ${s.dur}s ease-in-out ${s.delay}s infinite` }} />
          ))}
        </div>
        <div style={{ position: "absolute", top: "-50%", left: "6%", width: 220, height: 220, borderRadius: "50%", background: "radial-gradient(circle,rgba(185,28,28,0.44) 0%,transparent 70%)", filter: "blur(36px)", animation: "crOrb1 14s ease-in-out infinite", pointerEvents: "none" }} />
        <div style={{ position: "absolute", top: "-38%", right: "10%", width: 170, height: 170, borderRadius: "50%", background: "radial-gradient(circle,rgba(239,68,68,0.28) 0%,transparent 70%)", filter: "blur(28px)", animation: "crOrb2 17s ease-in-out infinite", pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: "-24%", left: "44%", width: 130, height: 130, borderRadius: "50%", background: "radial-gradient(circle,rgba(252,165,165,0.18) 0%,transparent 70%)", filter: "blur(22px)", animation: "crOrb3 11s ease-in-out infinite", pointerEvents: "none" }} />

        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px 6px", position: "relative", zIndex: 2 }}>
          <div onClick={() => navigate(`/MetaCommerce/${userId}`)} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", flexShrink: 0 }}>
            <div style={{ padding: 2, borderRadius: 10, background: T.grad, display: "flex" }}>
              <img src={logo} alt="CosMeta" style={{ width: 26, height: 26, borderRadius: 8, background: "#fff", objectFit: "contain", padding: 3 }} onError={e => { (e.currentTarget as HTMLImageElement).style.display = "none"; }} />
            </div>
          </div>
          <span className="cr-shimmer-title" style={{ fontFamily: "'Playfair Display',serif", fontSize: isMobile ? 15 : 18, fontWeight: 700, letterSpacing: -0.3, flexShrink: 0 }}>Car Electronics</span>
          {!locationLoading && !locationDenied && userCountry && (
            <button onClick={handleToggleGlobal} style={{ display: "flex", alignItems: "center", gap: 5, background: showGlobal ? "rgba(255,255,255,.06)" : "rgba(252,165,165,.15)", border: `1px solid ${showGlobal ? "rgba(255,255,255,.18)" : "rgba(252,165,165,.44)"}`, borderRadius: 8, padding: "5px 10px", color: showGlobal ? "rgba(252,165,165,.52)" : T.racingLight, fontSize: 11, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>
              {showGlobal ? <Globe size={11} /> : <MapPin size={11} />}
              <span style={{ display: isMobile ? "none" : "inline" }}>{showGlobal ? "Global" : userCity || userCountry}</span>
            </button>
          )}
          {locationLoading && (
            <div style={{ display: "flex", alignItems: "center", gap: 5, background: "rgba(255,255,255,.06)", borderRadius: 8, padding: "5px 10px", flexShrink: 0 }}>
              <div style={{ width: 5, height: 5, borderRadius: "50%", background: T.racingLight, animation: "cr-pulse 1.2s infinite" }} />
              <span style={{ fontSize: 10, color: "rgba(252,165,165,.44)" }}>Locating…</span>
            </div>
          )}
          <div style={{ flex: 1 }} />
          <button onClick={() => navigate(`/MetaCommerce/${userId}`)} style={{ background: "rgba(255,255,255,.07)", border: "1px solid rgba(252,165,165,.22)", borderRadius: 8, padding: "7px 10px", cursor: "pointer", display: "flex", alignItems: "center", color: "#fff", flexShrink: 0 }}><Home size={16} /></button>
          <div style={{ position: "relative", cursor: "pointer", flexShrink: 0 }} onClick={() => navigate(`/notifications/${userId}`)}>
            <Notifications style={{ fontSize: 22, color: T.racingLight }} />
            {unreadCount > 0 && <span style={{ position: "absolute", top: -4, right: -4, background: T.globalRed, color: "#fff", fontSize: 8, fontWeight: 900, width: 15, height: 15, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>{unreadCount}</span>}
          </div>
          <button onClick={goToCheckout} style={{ position: "relative", background: cartTotal > 0 ? T.racing : "rgba(255,255,255,.07)", border: `1px solid ${cartTotal > 0 ? "transparent" : "rgba(252,165,165,.22)"}`, borderRadius: 8, padding: "7px 10px", cursor: "pointer", display: "flex", alignItems: "center", gap: 5, color: "#fff", fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
            <ShoppingCart size={16} />
            {!isMobile && cartTotal > 0 && <span>{cartTotal}</span>}
            {cartTotal > 0 && <span style={{ position: "absolute", top: -4, right: -4, background: T.red, color: "#fff", fontSize: 8, fontWeight: 900, width: 15, height: 15, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>{cartTotal > 99 ? "99+" : cartTotal}</span>}
          </button>
          <button onClick={() => setNavOpen(v => !v)} style={{ background: "rgba(255,255,255,.07)", border: "1px solid rgba(252,165,165,.22)", borderRadius: 8, padding: "7px 10px", cursor: "pointer", display: "flex", alignItems: "center", color: "#fff", flexShrink: 0 }}>{navOpen ? <X size={16} /> : <Menu size={16} />}</button>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "0 16px 12px", position: "relative", zIndex: 2 }}>
          <div style={{ display: "flex", alignItems: "center", flex: 1, background: "rgba(252,165,165,.08)", borderRadius: 10, overflow: "hidden", border: `1.5px solid ${searchFocused ? "rgba(252,165,165,.65)" : "rgba(252,165,165,.20)"}`, backdropFilter: "blur(8px)", boxShadow: searchFocused ? "0 0 0 3px rgba(185,28,28,.20)" : "none", transition: "border-color .2s,box-shadow .2s" }}>
            <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} onFocus={() => setSearchFocused(true)} onBlur={() => setSearchFocused(false)} placeholder="Search dash cams, GPS, car audio, radar detectors…" style={{ flex: 1, border: "none", outline: "none", padding: "10px 14px", fontSize: 13, color: "#fff", background: "transparent" }} />
            {searchQuery && <button onClick={() => setSearchQuery("")} style={{ background: "none", border: "none", cursor: "pointer", padding: "0 8px", color: "rgba(252,165,165,.48)" }}><X size={13} /></button>}
            <div style={{ width: 42, height: 42, background: T.grad, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Search size={15} color="#fff" /></div>
          </div>
          <button ref={sortBtnRef} onClick={toggleSort} style={{ display: "flex", alignItems: "center", gap: 5, background: sortOpen ? T.racing : "rgba(255,255,255,.07)", border: `1px solid ${sortOpen ? T.racing : "rgba(252,165,165,.22)"}`, borderRadius: 8, padding: "10px 12px", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer", flexShrink: 0, whiteSpace: "nowrap" }}>
            <SlidersHorizontal size={13} />
            {!isMobile && <span>{activeSort !== "default" ? SORT_OPTS.find(o => o.key === activeSort)?.label?.split(":")[0] : "Sort"}</span>}
            {sortOpen ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
          </button>
        </div>
        <div className="cr-grad-line" style={{ height: 2, position: "relative", zIndex: 2 }} />
      </div>

      {sortOpen && sortAnchor && (
        <>
          <div style={{ position: "fixed", inset: 0, zIndex: 9998 }} onClick={() => { setSortOpen(false); setSortAnchor(null); }} />
          <div style={{ position: "fixed", top: sortAnchor.top, left: sortAnchor.left, background: "#fff", borderRadius: 14, boxShadow: "0 20px 56px rgba(0,0,0,.22)", zIndex: 9999, minWidth: 200, overflow: "hidden", border: T.racingOutline }}>
            {SORT_OPTS.map(opt => (
              <div key={opt.key} className="cr-sort-item" onClick={() => { setActiveSort(opt.key); setSortOpen(false); setSortAnchor(null); }} style={{ padding: "11px 18px", fontSize: 13, fontWeight: activeSort === opt.key ? 700 : 500, color: activeSort === opt.key ? T.racing : NAVY, background: activeSort === opt.key ? T.bgMuted : "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #FECACA" }}>
                {opt.label}{activeSort === opt.key && <span style={{ color: T.racing }}>✓</span>}
              </div>
            ))}
          </div>
        </>
      )}

      <AnimatePresence>
        {navOpen && (
          <>
            <div style={{ position: "fixed", inset: 0, zIndex: 599 }} onClick={() => setNavOpen(false)} />
            <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} style={{ position: "fixed", top: 70, right: 16, width: 220, zIndex: 600, background: "#fff", borderRadius: 18, boxShadow: "0 24px 64px rgba(0,0,0,.20)", border: T.racingOutline, overflow: "hidden" }}>
              {[
                { label: "Profile", icon: <User size={14} />, path: `/profile/${userId}` },
                { label: "Orders",  icon: <ShoppingBag size={14} />, path: `/MetaClientDashboard/${userId}` },
                { label: "Sell",    icon: <Tag size={14} />, path: `/metaonboarding/${userId}` },
                { label: "Support", icon: <SupportAgent style={{ fontSize: 14 }} />, path: `/CustomerSupport/${userId}` },
                { label: "Chat",    icon: <ChatBubble style={{ fontSize: 14 }} />, path: "/MessagesPreview" },
              ].map(item => (
                <button key={item.label} style={{ display: "flex", alignItems: "center", gap: 12, padding: "13px 18px", cursor: "pointer", fontSize: 13, fontWeight: 700, color: NAVY, borderBottom: "1px solid #FECACA", background: "none", width: "100%", textAlign: "left", border: "none" }} onClick={() => { navigate(item.path); setNavOpen(false); }}>
                  <span style={{ color: T.racing }}>{item.icon}</span>{item.label}
                </button>
              ))}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );

  /* ── HERO ── */
  const HeroStrip = () => (
    <div style={{ position: "relative", overflow: "hidden", background: "#0a0000", padding: isMobile ? "28px 16px 24px" : "44px 36px 36px" }}>
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
        {STARS.map(s => (<div key={s.id} style={{ position: "absolute", left: `${s.x}%`, top: `${s.y}%`, width: s.size, height: s.size, borderRadius: "50%", background: s.warm ? "rgba(252,165,165,0.75)" : "rgba(255,255,230,0.55)", animation: `starTwinkle ${s.dur}s ease-in-out ${s.delay}s infinite` }} />))}
      </div>
      <div style={{ position: "absolute", top: "12%", left: "3%", width: 360, height: 360, borderRadius: "50%", background: "radial-gradient(circle,rgba(185,28,28,0.22) 0%,transparent 70%)", filter: "blur(72px)", animation: "crOrb1 16s ease-in-out infinite", pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: "-18%", right: "5%", width: 290, height: 290, borderRadius: "50%", background: "radial-gradient(circle,rgba(239,68,68,0.16) 0%,transparent 70%)", filter: "blur(62px)", animation: "crOrb2 21s ease-in-out infinite", pointerEvents: "none" }} />

      {/* speed lines (desktop) */}
      {!isMobile && (
        <div style={{ position: "absolute", right: 56, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", zIndex: 1 }}>
          {SPEED_LINES.map((top, i) => (
            <div key={i} style={{ position: "absolute", right: 0, width: 48 + i * 12, height: 2, borderRadius: 1, background: `linear-gradient(90deg,transparent,rgba(185,28,28,${0.4 - i * 0.06}))`, top, animation: `crSpeedLine ${0.8 + i * 0.15}s ease-out ${i * 0.18}s infinite`, transformOrigin: "right center" }} />
          ))}
          <Car size={52} color="rgba(185,28,28,0.22)" strokeWidth={1.2} style={{ position: "absolute", right: 52, top: "50%", transform: "translateY(-50%)", animation: "crRev 4s ease-in-out infinite" }} />
        </div>
      )}

      {/* carbon fibre weave */}
      <div style={{ position: "absolute", inset: 0, backgroundImage: "repeating-linear-gradient(45deg,rgba(185,28,28,0.04) 0px,rgba(185,28,28,0.04) 1px,transparent 1px,transparent 14px),repeating-linear-gradient(-45deg,rgba(185,28,28,0.04) 0px,rgba(185,28,28,0.04) 1px,transparent 1px,transparent 14px)", pointerEvents: "none", zIndex: 1 }} />

      <div style={{ position: "relative", zIndex: 2 }}>
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <div style={{ width: 24, height: 2, background: T.racing, borderRadius: 1 }} />
          <p style={{ fontSize: 9, letterSpacing: "0.32em", color: T.racingLight, fontFamily: "'DM Sans',sans-serif", fontWeight: 700 }}>🚗 CAR ELECTRONICS</p>
        </motion.div>
        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="cr-shimmer-title" style={{ fontFamily: "'Playfair Display',serif", fontSize: isMobile ? "clamp(26px,7vw,38px)" : "clamp(38px,4.5vw,62px)", fontWeight: 700, lineHeight: 1.06, letterSpacing: "-0.02em", marginBottom: 10 }}>
          Drive with<br /><em style={{ fontStyle: "italic", fontWeight: 400 }}>Intelligence</em>
        </motion.h1>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.35 }} style={{ fontSize: 13, color: "rgba(252,165,165,0.50)", fontFamily: "'DM Sans',sans-serif", fontWeight: 300, maxWidth: 500 }}>
          {locationDenied ? "Showing all global car electronics" : showGlobal ? "Browsing worldwide dash cams, GPS & car audio" : userCity ? `Car electronics near ${userCity}` : "Discovering car electronics near you"}
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} style={{ display: "flex", gap: 28, marginTop: 22, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
              <span style={{ fontSize: isMobile ? 32 : 44, fontWeight: 900, color: "#fff", fontFamily: "'Playfair Display',serif", lineHeight: 1 }}>{carProducts.length}</span>
              <span style={{ fontSize: 10, color: "rgba(252,165,165,.40)", fontFamily: "'DM Sans',sans-serif", letterSpacing: "0.12em" }}>LISTINGS</span>
            </div>
          </div>
          {!locationDenied && !showGlobal && userCountry && (
            <div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                <span style={{ fontSize: isMobile ? 20 : 28, fontWeight: 900, color: T.racingLight, fontFamily: "'Playfair Display',serif", lineHeight: 1 }}>{userCountry}</span>
                <span style={{ fontSize: 10, color: "rgba(252,165,165,.40)", fontFamily: "'DM Sans',sans-serif", letterSpacing: "0.12em" }}>LOCAL</span>
              </div>
            </div>
          )}
          <div style={{ marginLeft: "auto", opacity: 0.10 }}><Car size={isMobile ? 60 : 86} color="#B91C1C" /></div>
        </motion.div>
      </div>
    </div>
  );

  /* ── CARD ── */
  const CarCard = ({ item }: { item: any }) => {
    const qty = getQty(item.listingId);
    const price = fmtEur(item.rate ?? 0);
    const discount = typeof item.discount === "number" && item.discount > 0 ? item.discount : null;
    const [hovered, setHovered] = useState(false);

    if (isMobile) {
      return (
        <motion.div whileTap={{ scale: 0.95 }} style={{ borderRadius: 18, overflow: "hidden", border: T.racingOutline, boxShadow: `0 4px 20px ${T.racingDim}`, background: "#fff", cursor: "pointer" }} onClick={() => goToDetails(item.listingId)}>
          <div style={{ width: "100%", aspectRatio: "1/1", position: "relative", overflow: "hidden", background: "linear-gradient(135deg,rgba(185,28,28,.18),rgba(185,28,28,.06))" }}>
            {item.photos?.[0] ? <img src={item.photos[0]} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}><Car size={34} color={T.racing} /></div>}
            <div style={{ position: "absolute", inset: 0, background: `linear-gradient(to top,${NAVY}F2,transparent 50%)` }} />
            {discount && <div style={{ position: "absolute", top: 7, left: 7, background: T.red, color: "#fff", fontSize: 9, fontWeight: 800, padding: "2px 7px", borderRadius: 6, zIndex: 5 }}>{discount}% OFF</div>}
            <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "8px 10px 10px", zIndex: 3, pointerEvents: "none" }}>
              <div style={{ fontWeight: 800, fontSize: 10, color: "#fff", lineHeight: 1.25, marginBottom: 2, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as any, overflow: "hidden" }}>{item.name}</div>
              <span style={{ color: T.red, fontWeight: 900, fontSize: 16 }}>{price}</span>
            </div>
            <div style={{ position: "absolute", top: 7, right: 7, zIndex: 10 }} onClick={e => e.stopPropagation()}>
              {qty > 0 ? (
                <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} style={{ display: "flex", alignItems: "center", gap: 0, background: "rgba(18,6,6,.92)", borderRadius: 20, padding: "4px 6px", backdropFilter: "blur(8px)", border: "1.5px solid rgba(252,165,165,.28)" }}>
                  <button style={{ width: 26, height: 26, borderRadius: "50%", border: "none", background: "rgba(255,255,255,.18)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }} onClick={e => { e.stopPropagation(); dispatch(decrementCartItem({ listingId: item.listingId })); }}><Minus size={11} color="#fff" /></button>
                  <span style={{ color: "#fff", fontWeight: 900, minWidth: 20, textAlign: "center", fontSize: 12 }}>{qty}</span>
                  <button style={{ width: 26, height: 26, borderRadius: "50%", border: "none", background: "rgba(255,255,255,.28)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }} onClick={e => { e.stopPropagation(); handleAddToCart(item); }}><Plus size={11} color="#fff" /></button>
                </motion.div>
              ) : (
                <motion.button whileTap={{ scale: 0.88 }} onClick={e => { e.stopPropagation(); handleAddToCart(item); }} style={{ width: 38, height: 38, borderRadius: "50%", border: "none", background: T.red, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}><ShoppingCartOutlined style={{ fontSize: 18, color: "#fff" }} /></motion.button>
              )}
            </div>
          </div>
        </motion.div>
      );
    }

    return (
      <motion.div className="cr-card-hover" style={{ borderRadius: 20, overflow: "hidden", border: T.racingOutline, boxShadow: `0 6px 28px ${T.racingDim}`, background: "#fff", cursor: "pointer" }} onClick={() => goToDetails(item.listingId)} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
        <div style={{ width: "100%", aspectRatio: "1/1", position: "relative", overflow: "hidden", background: "linear-gradient(135deg,rgba(185,28,28,.14),rgba(185,28,28,.04))" }}>
          {item.photos?.[0] ? <img src={item.photos[0]} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform .38s cubic-bezier(.34,1.56,.64,1)", transform: hovered ? "scale(1.08)" : "scale(1)" }} /> : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}><Car size={52} color={T.racing} style={{ opacity: 0.3 }} /></div>}
          <div style={{ position: "absolute", inset: 0, background: `linear-gradient(to top,${NAVY}F5 0%,rgba(18,6,6,0.70) 38%,transparent 65%)`, pointerEvents: "none" }} />
          {discount && <div style={{ position: "absolute", top: 8, left: 8, background: T.red, color: "#fff", fontSize: 10, fontWeight: 800, padding: "3px 8px", borderRadius: 7, zIndex: 5 }}>{discount}% OFF</div>}

          {/* RPM indicator badge */}
          <div style={{ position: "absolute", top: discount ? 44 : 10, left: 10, background: "rgba(18,6,6,.84)", borderRadius: 6, padding: "3px 7px", zIndex: 5, border: "1px solid rgba(252,165,165,.18)", display: "flex", alignItems: "center", gap: 4 }}>
            <div style={{ width: 4, height: 4, borderRadius: "50%", background: T.racing, animation: "cr-pulse 1.8s infinite", boxShadow: `0 0 4px ${T.racing}` }} />
            <span style={{ fontSize: 8, color: T.racingLight, fontWeight: 700, letterSpacing: "0.06em" }}>ON</span>
          </div>

          <div style={{ position: "absolute", top: 8, right: 8, zIndex: 10 }} onClick={e => e.stopPropagation()}>
            {qty > 0 ? (
              <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} style={{ display: "flex", alignItems: "center", background: "rgba(18,6,6,.92)", borderRadius: 22, padding: "3px 5px", backdropFilter: "blur(8px)", border: "1.5px solid rgba(252,165,165,.28)", gap: 0 }}>
                <button style={{ width: 30, height: 30, borderRadius: "50%", border: "none", background: "rgba(255,255,255,.18)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }} onClick={e => { e.stopPropagation(); dispatch(decrementCartItem({ listingId: item.listingId })); }}><Minus size={12} color="#fff" /></button>
                <span style={{ color: "#fff", fontWeight: 900, minWidth: 24, textAlign: "center", fontSize: 14 }}>{qty}</span>
                <button style={{ width: 30, height: 30, borderRadius: "50%", border: "none", background: "rgba(255,255,255,.28)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }} onClick={e => { e.stopPropagation(); handleAddToCart(item); }}><Plus size={12} color="#fff" /></button>
              </motion.div>
            ) : (
              <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} onClick={e => { e.stopPropagation(); handleAddToCart(item); }} style={{ width: 48, height: 48, borderRadius: "50%", border: "none", background: T.red, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: "0 3px 14px rgba(229,9,20,.55)" }}><ShoppingCartOutlined style={{ fontSize: 22, color: "#fff" }} /></motion.button>
            )}
          </div>
          <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "12px 14px 14px", zIndex: 3, pointerEvents: "none" }}>
            <div style={{ fontWeight: 800, fontSize: 12, color: "#fff", lineHeight: 1.25, marginBottom: 5, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as any, overflow: "hidden", letterSpacing: -0.2 }}>{item.name}</div>
            <span style={{ color: T.red, fontWeight: 900, fontSize: 22, letterSpacing: -0.5, lineHeight: 1 }}>{price}</span>
          </div>
        </div>
      </motion.div>
    );
  };

  /* ── VARIANT MODAL ── */
  const VariantModal = () => (
    <AnimatePresence>
      {variantOpen && variantItem && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setVariantOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", zIndex: 999, display: "flex", alignItems: "flex-end", justifyContent: "center", backdropFilter: "blur(8px)" }}>
          <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 30, stiffness: 300 }} onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: "28px 28px 0 0", padding: 24, width: "100%", maxWidth: 480, maxHeight: "90vh", overflowY: "auto" }}>
            <div style={{ width: 44, height: 4, borderRadius: 2, background: "#FECACA", margin: "0 auto 22px" }} />
            <div style={{ display: "flex", gap: 14, marginBottom: 20 }}>
              {variantItem.photos?.[0] && (
                <div style={{ width: 72, height: 72, borderRadius: 14, overflow: "hidden", background: "#FFF5F5", flexShrink: 0 }}>
                  <img src={variantItem.variants?.[previewColorId!]?.imageUrl ?? variantItem.photos[0]} alt={variantItem.name} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                </div>
              )}
              <div>
                <h3 style={{ margin: "0 0 4px", fontWeight: 800, fontSize: 15, color: NAVY }}>{variantItem.name}</h3>
                <span style={{ color: T.red, fontWeight: 900, fontSize: 18 }}>{fmtEur(variantItem.rate ?? 0)}</span>
              </div>
            </div>
            {variantMode && (
              <>
                <h4 style={{ margin: "0 0 10px", fontSize: 12, fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: 0.8 }}>{variantMode === "beauty" ? "Select Shade" : "Select Color"}</h4>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
                  {Object.keys(variantItem.variants || {}).map(id => {
                    const colorId = Number(id);
                    const color = electronicsColorAttributes.values.find((v: any) => v.id === colorId);
                    return <button key={colorId} onClick={() => { setSelectedColor(colorId); if (variantMode === "fashion") setSelectedSize(null); }} style={{ width: 34, height: 34, borderRadius: "50%", border: selectedColor === colorId ? `3px solid ${NAVY}` : "2px solid #ddd", background: color?.hexCodes?.[0] ?? "#eee", cursor: "pointer", transform: selectedColor === colorId ? "scale(1.15)" : "scale(1)", boxShadow: selectedColor === colorId ? `0 0 0 2px #fff,0 0 0 4px ${T.racing}` : "none", transition: "all .15s" }} />;
                  })}
                </div>
                {variantMode === "fashion" && selectedColor !== null && (
                  <>
                    <h4 style={{ margin: "0 0 10px", fontSize: 12, fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: 0.8 }}>Select Size</h4>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
                      {(variantItem.variants?.[selectedColor]?.sizeIds ?? []).map((sizeId: number) => {
                        const size = electronicsSizeAttributes.values.find((v: any) => v.id === sizeId);
                        return <button key={sizeId} onClick={() => setSelectedSize(sizeId)} style={{ padding: "8px 16px", borderRadius: 10, border: `2px solid ${selectedSize === sizeId ? NAVY : "#FECACA"}`, fontWeight: 700, fontSize: 13, cursor: "pointer", background: selectedSize === sizeId ? NAVY : "#fff", color: selectedSize === sizeId ? "#fff" : NAVY, transition: "all .15s" }}>{size?.name}</button>;
                      })}
                    </div>
                  </>
                )}
              </>
            )}
            {selectedColor !== null && (variantMode === "beauty" || (variantMode === "fashion" && selectedSize !== null)) && (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 20, marginBottom: 20, background: "#FFF5F5", borderRadius: 14, padding: "12px 16px" }}>
                <button onClick={() => setVariantQty(q => Math.max(1, q - 1))} style={{ width: 38, height: 38, borderRadius: 10, border: "2px solid #FECACA", background: "#fff", display: "grid", placeItems: "center", cursor: "pointer" }}><Minus size={14} color={NAVY} /></button>
                <span style={{ fontSize: 22, fontWeight: 900, color: NAVY, minWidth: 30, textAlign: "center" }}>{variantQty}</span>
                <button onClick={() => setVariantQty(q => q + 1)} style={{ width: 38, height: 38, borderRadius: 10, border: `2px solid ${T.racing}`, background: T.racing, display: "grid", placeItems: "center", cursor: "pointer" }}><Plus size={14} color="#fff" /></button>
              </div>
            )}
            <div style={{ display: "flex", gap: 10 }}>
              <button disabled={!canContinue} onClick={() => handleVariantContinue(false)} style={{ flex: 1, padding: 14, borderRadius: 14, border: `2px solid ${canContinue ? NAVY : "#FECACA"}`, background: canContinue ? NAVY : "#FFF5F5", color: canContinue ? "#fff" : T.muted, fontWeight: 800, fontSize: 14, cursor: canContinue ? "pointer" : "not-allowed" }}>Add to Cart</button>
              <button disabled={!canContinue} onClick={() => handleVariantContinue(true)} style={{ flex: 1, padding: 14, borderRadius: 14, border: "none", background: canContinue ? GRADIENT : "#FFF5F5", color: canContinue ? "#fff" : T.muted, fontWeight: 800, fontSize: 14, cursor: canContinue ? "pointer" : "not-allowed" }}>Buy Now</button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <div style={{ minHeight: "100vh", background: T.bgSoft, paddingBottom: 32, fontFamily: "'DM Sans',sans-serif" }}>
      <style>{COSMIC_CSS}</style>
      <Header />
      <HeroStrip />
      <div style={{ padding: isMobile ? "20px 12px" : "28px 28px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 4, height: 22, borderRadius: 2, background: GRADIENT }} />
            <span style={{ fontWeight: 900, fontSize: 15, color: NAVY }}>{locationDenied ? "All Car Electronics" : showGlobal ? "Global Car Electronics" : userCity ? `Car Electronics near ${userCity}` : userCountry ?? "All Car Electronics"}</span>
            <span style={{ fontSize: 12, color: T.muted, background: T.bgMuted, padding: "2px 9px", borderRadius: 20 }}>{carProducts.length}</span>
          </div>
          {!locationDenied && userCountry && (
            <button onClick={handleToggleGlobal} style={{ display: "flex", alignItems: "center", gap: 5, background: showGlobal ? "#FEE2E2" : T.bgSoft, borderRadius: 20, padding: "5px 13px", border: `1.5px solid ${showGlobal ? "#FECACA" : T.racingLight}`, fontSize: 11, fontWeight: 700, color: showGlobal ? T.muted : T.racing, cursor: "pointer" }}>
              {showGlobal ? <MapPin size={11} /> : <Globe size={11} />}{showGlobal ? "Show Local" : "Show Global"}
            </button>
          )}
        </div>
        {carProducts.length > 0 ? (
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2,1fr)" : "repeat(auto-fill,minmax(210px,1fr))", gap: isMobile ? 12 : 18 }}>
            {carProducts.map((item: any) => <CarCard key={item.listingId} item={item} />)}
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: "72px 24px" }}>
            <div style={{ display: "inline-block", marginBottom: 24 }}>
              <div style={{ width: 88, height: 88, borderRadius: "50%", background: GRADIENT, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 8px 32px rgba(185,28,28,.35)" }}>
                <Car size={40} color="#fff" />
              </div>
            </div>
            <h3 style={{ fontWeight: 900, color: NAVY, marginBottom: 8, fontSize: 18, fontFamily: "'Playfair Display',serif" }}>{locationDenied ? "Location needed" : "No car electronics found"}</h3>
            <p style={{ color: T.muted, marginBottom: 24, fontSize: 13 }}>{locationDenied ? "Enable location to see local car electronics." : searchQuery ? `No results for "${searchQuery}"` : "No car electronics listed in your area yet."}</p>
            <button onClick={() => { setSearchQuery(""); setActiveSort("default"); if (!showGlobal) handleToggleGlobal(); }} style={{ padding: "12px 28px", borderRadius: 12, border: "none", background: GRADIENT, color: "#fff", fontWeight: 800, cursor: "pointer", fontSize: 13 }}>Browse Global Car Electronics</button>
          </div>
        )}
      </div>
      <VariantModal />
    </div>
  );
};

export default CarElectronics;
